import { Badge, Divider } from "antd";
import { cloneDeep } from "lodash";
import { ForwardedRef, forwardRef, ReactNode, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import './styles.css';
import { isNullOrEmpty } from "../../../../utils/utils";
import CircleButton from "../../CircleButton/CircleButton";
import useDevice from "../../../../hooks/useDevice";
import FAIcon from "../../FAIcon/FAIcon";
import SpeedDial from "../../SpeedDial/SpeedDial";
import useWindow from "../../../../hooks/useWindow";

export type ViewAddonsExtraType = ReactNode | { element: ReactNode, hideMobile?: boolean; };

export interface ViewAddonsRef {
    setContent: (content: ReactNode) => void;
    setExtra: (content: ViewAddonsExtraType[]) => void;
    setSidebars: (content: Sidebar[]) => void;
    reset: () => void;
    subscribe: (callback: SubscribeCallback) => () => void;
    setSidebarType: (type: SidebarType) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export enum SidebarEnum {
    NONE = 'None',
    FILTERS = 'Filters',
    SETTINGS = "Settings"
}

export type SidebarType = SidebarEnum | string;

export interface SidebarButton {
    title: string;
    icon: React.ReactNode;
}

export interface Sidebar {
    type: SidebarType;
    button: SidebarButton;
    badge?: boolean | number;
    titleBar?: {
        title?: React.ReactNode;
        icon?: React.ReactNode;
        extra?: React.ReactNode;
    };
    content: React.ReactNode;
}

export type SubscribeCallback = (type: SidebarType, tabSidebars: Sidebar[]) => void;

interface Props {
    setOpenDrawer: (open: boolean) => void;
}

const ViewAddonsRenderer = forwardRef((props: Props, ref: ForwardedRef<ViewAddonsRef>) => {
    const intl = useIntl();
    const { width, isSmartphone } = useDevice();
    const window = useWindow();

    const [sidebarType, setSidebarType] = useState<SidebarType>(SidebarEnum.NONE);
    const [content, setContent] = useState<ReactNode>();
    const [extra, setExtra] = useState<ViewAddonsExtraType[]>([]);
    const [sidebars, setSidebars] = useState<Sidebar[]>([]);

    const subscribersRef = useRef<Set<SubscribeCallback>>(new Set());

    const notifySubscribers = useCallback((type: SidebarType, tabSidebars: Sidebar[]) => {
        subscribersRef.current.forEach(callback => callback(type, tabSidebars));
    }, []);

    const subscribe = useCallback((callback: SubscribeCallback) => {
        subscribersRef.current.add(callback);
        return () => subscribersRef.current.delete(callback);
    }, []);

    const setSidebarsAndNotify = useCallback((content: Sidebar[]) => {
        setSidebars(content);
        notifySubscribers(sidebarType, content);
    }, [notifySubscribers, sidebarType]);

    const reset = useCallback(() => {
        setExtra([]);
        setContent(undefined);
        setSidebars([]);
        notifySubscribers(sidebarType, []);
    }, [notifySubscribers, sidebarType]);

    useImperativeHandle(ref, (): ViewAddonsRef => ({
        setExtra,
        setContent,
        setSidebars: setSidebarsAndNotify,
        setSidebarType,
        reset,
        subscribe,
    }), [reset, setSidebarsAndNotify, subscribe]);

    const sidebarsButtons = useMemo((): ReactNode[] => {
        if (!isNullOrEmpty(sidebars)) {
            return sidebars.map((s, idx) => (
                <CircleButton
                    key={`sidebar-button-${s.type}-${idx}`}
                    type={s.type === sidebarType ? 'primary' : 'default'}
                    onClick={() => {
                        //TODO: KROK Check if there is a better solution than `type as any` here
                        const type = s.type === sidebarType ? SidebarEnum.NONE : s.type;
                        const isSidebarEnum = Object.values(SidebarEnum).includes(type as any);

                        notifySubscribers(type, sidebars);
                        window.updateSidebarType(isSidebarEnum ? type as any : SidebarEnum.NONE);
                        setSidebarType(type);
                    }}
                    title={s.button.title}
                    icon={
                        typeof s.badge === 'boolean' ?
                            <Badge
                                className='__viewaddons-renderer-button-badge'
                                dot={s.badge}
                            >
                                {s.button.icon}
                            </Badge>
                            :
                            <Badge
                                className='__viewaddons-renderer-button-badge'
                                count={s.badge}
                            >
                                {s.button.icon}
                            </Badge>
                    }
                />
            ));
        }
        return [];
    }, [notifySubscribers, sidebarType, sidebars, window]);


    const hasExtra = useMemo(() => {
        return extra.length > 0;
    }, [extra]);

    const hasSidebars = useMemo(() => {
        return sidebars.length > 0;
    }, [sidebars.length]);

    const hasContent = useMemo(() => {
        return content !== undefined;
    }, [content]);

    const isVisible = useMemo(() => {
        return hasExtra || hasSidebars || hasContent;
    }, [hasContent, hasExtra, hasSidebars]);


    const extraRender = useCallback(() => {
        const collapseWidth = 500;

        const extraMobileVisibleElements: ReactNode[] = [];
        const extraMobileHiddenElements: ReactNode[] = [];

        const extraElements = extra.map((item) => {
            if (item && typeof item === 'object' && 'element' in item) {

                if ('hideMobile' in item && item.hideMobile)
                    extraMobileVisibleElements.push(item.element);
                else
                    extraMobileHiddenElements.push(item.element);

                return item.element;
            }

            extraMobileVisibleElements.push(item);
            return item;
        });

        if ((extraMobileVisibleElements.length > 0 || hasSidebars) && collapseWidth > width) {
            let buttons = cloneDeep(extraMobileVisibleElements);

            if (extraMobileVisibleElements.length > 0 && hasSidebars) {
                buttons.push(<Divider type='vertical' dashed />);
            }

            buttons = buttons.concat(sidebarsButtons);

            return (
                <SpeedDial
                    title={intl.formatMessage({ defaultMessage: 'More actions' })}
                    small
                    icon={<FAIcon prefix='fad' name='ellipsis-vertical' />}
                    openIcon={<FAIcon prefix='fad' name='ellipsis' />}
                    buttons={buttons} />
            );
        }

        return (
            <>
                {extraElements}
                {hasExtra && hasSidebars ? <Divider type='vertical' dashed /> : undefined}
                {sidebarsButtons}
            </>
        );

    }, [extra, hasExtra, hasSidebars, intl, sidebarsButtons, width]);

    return (
        <div className={`__viewaddons-renderer ${isVisible || isSmartphone ? '' : 'hidden'}`}>
            <div className='__viewaddons-main'>
                {content}
            </div>
            <div className='__viewaddons-extra'>
                {extraRender()}
                {
                    isSmartphone ?
                        <CircleButton
                            icon={<FAIcon prefix='fad' name='bars' />}
                            onClick={() => props.setOpenDrawer(true)}
                        />
                        :
                        undefined
                }
            </div>
        </div>
    );
});
ViewAddonsRenderer.displayName = 'VARenderer';

export default ViewAddonsRenderer;